import { useQueries, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { RefreshCw01 } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { DisplayInput } from "@/components/Atoms/DisplayField";
import {
	DisplayFieldFeedback,
	DisplayFieldState,
} from "@/components/Atoms/DisplayField/display-field.shared";
import {
	DisplaySelect,
	DisplaySelectBadgeVariant,
	DisplaySelectValueType,
} from "@/components/Atoms/DisplayField/DisplaySelect";
import { useFieldFeedbackMap } from "@/components/Atoms/FeedbackIndicator/use-field-feedback-map";
import { Button } from "@/components/base/buttons/button";
import { fieldLessThanLength } from "@/components/form/validation/create-common-validators";
import { ConfirmationModal } from "@/components/Organisms/ConfirmationModal/ConfirmationModal";
import { usePlatformUserEditAccess } from "@/features/users/platform-user-edit-access";
import { PlatformUserStatusActionModal } from "@/features/users/platform-user-status-action-modal";
import {
	PLATFORM_USER_STATUS_ACTIONS,
	type PlatformUserStatusAction,
	platformUserStatusActionName,
	platformUserStatusActionsFromPermissions,
	platformUserStatusApiValue,
} from "@/features/users/platform-user-status-actions";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth";
import { useForcePasswordReset, userQueryOptions } from "@/services/user";
import {
	useUpdateUserInfo,
	useUpdateUserStatus,
} from "@/services/user/user.mutations";
import { formatId } from "@/utils/format-id";
import { showApiErrorToast, showSuccessToast } from "@/utils/toast.utils";
import { SearchSchema } from "../$userId";
export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/users/(organization-users)/organization/$userId/details",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

// Status helper components
export const StatusCircle = ({
	color,
}: {
	color: "success" | "warning" | "gray" | "error";
}) => {
	const colorMap = {
		success: "bg-success-500",
		warning: "bg-warning-500",
		gray: "bg-gray-500",
		error: "bg-error-500",
	};
	return (
		<span className={`inline-block size-3.5 rounded-full ${colorMap[color]}`} />
	);
};

export const ActiveIcon = ({ className }: { className?: string }) => (
	<span className={className}>
		<StatusCircle color="success" />
	</span>
);

export const SuspendedIcon = ({ className }: { className?: string }) => (
	<span className={className}>
		<StatusCircle color="error" />
	</span>
);

export const ArchivedIcon = ({ className }: { className?: string }) => (
	<span className={className}>
		<StatusCircle color="gray" />
	</span>
);

export const RetiredIcon = ({ className }: { className?: string }) => (
	<span className={className}>
		<StatusCircle color="warning" />
	</span>
);

type NameField = "firstName" | "lastName";

const STATUS_BADGE_COLORS = {
	Active: "success",
	Suspended: "warning",
	Archived: "gray",
	Retired: "error",
} as const;

function RouteComponent() {
	const { t } = useAppTranslation();
	const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/users/(organization-users)/organization/$userId/details",
	});
	const navigate = useNavigate();
	const userById = useQuery(userQueryOptions.detail(params.userId));
	const forcePasswordReset = useForcePasswordReset();

	const canForcePasswordReset = useQuery(
		authQueryOptions.checkAction({
			name: "ForcePasswordReset",
			entityType: "User",
			entityId: params.userId,
		}),
	);

	/** At least one assigned org uses 1st-party auth (no OAuth provider on the organization). */
	const hasFirstPartyOrganizationMembership = useMemo(
		() =>
			userById.data?.organizationUserInfo?.some(
				(ou) =>
					ou.organization != null &&
					(ou.organization.oAuthProviderId == null ||
						ou.organization.oAuthProviderId === ""),
			) ?? false,
		[userById.data?.organizationUserInfo],
	);

	const showResetPasswordSection =
		canForcePasswordReset.data === true && hasFirstPartyOrganizationMembership;

	const handleResetPassword = async () => {
		try {
			await forcePasswordReset.mutateAsync(params.userId);
		} catch {
			return;
		}
		setIsResetPasswordOpen(false);
	};

	const { access: editAccess, isPending: editAccessPending } =
		usePlatformUserEditAccess(params.userId);
	const canEditName = editAccess.canEditProfile && !editAccessPending;
	const updateUser = useUpdateUserInfo();
	const updateStatus = useUpdateUserStatus();
	const { feedbackByField, setFieldFeedback } =
		useFieldFeedbackMap<NameField>();
	const [editingField, setEditingField] = useState<NameField | null>(null);
	const [draftByField, setDraftByField] = useState<
		Partial<Record<NameField, string>>
	>({});
	const [errorByField, setErrorByField] = useState<
		Partial<Record<NameField, string>>
	>({});
	const [pendingStatusAction, setPendingStatusAction] =
		useState<PlatformUserStatusAction | null>(null);
	const [retireEmail, setRetireEmail] = useState("");

	const statusActionChecks = useQueries({
		queries: PLATFORM_USER_STATUS_ACTIONS.map((action) =>
			authQueryOptions.checkAction({
				name: platformUserStatusActionName(action),
				entityType: "User",
				entityId: params.userId,
			}),
		),
	});
	const statusActions = platformUserStatusActionsFromPermissions(
		Object.fromEntries(
			PLATFORM_USER_STATUS_ACTIONS.map((action, index) => [
				action,
				statusActionChecks[index]?.data === true,
			]),
		),
	);

	const user = userById.data;
	const memberName =
		[user?.firstName, user?.lastName]
			.filter((part) => part?.trim())
			.join(" ")
			.trim() ||
		user?.displayName ||
		"";
	const statusName = user?.status?.name ?? "";
	const statusLabel =
		(statusName &&
			t(`admin.users.drawer.platform.statuses.${statusName.toLowerCase()}`)) ||
		statusName;
	const statusBadgeColor =
		STATUS_BADGE_COLORS[statusName as keyof typeof STATUS_BADGE_COLORS] ??
		"gray";
	const serverName = (field: NameField) => user?.[field] ?? "";

	const setNameError = (field: NameField, message: string | undefined) => {
		setErrorByField((prev) => ({ ...prev, [field]: message }));
	};
	const clearNameDraft = (field: NameField) => {
		setDraftByField((prev) => {
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};
	const beginNameEdit = (field: NameField) => {
		setNameError(field, undefined);
		setEditingField(field);
		setDraftByField((prev) => ({ ...prev, [field]: serverName(field) }));
	};
	const cancelNameEdit = (field: NameField) => {
		setNameError(field, undefined);
		setFieldFeedback(field, DisplayFieldFeedback.Idle);
		clearNameDraft(field);
		setEditingField((current) => (current === field ? null : current));
	};
	const commitName = async (field: NameField) => {
		const raw = draftByField[field];
		if (raw === undefined) {
			setEditingField((current) => (current === field ? null : current));
			return;
		}
		const next = raw.trim();
		if (next === serverName(field)) {
			cancelNameEdit(field);
			return;
		}
		const fieldLabel = t(
			field === "firstName" ? "User.FirstName" : "User.LastName",
			{ useFieldInfo: true },
		);
		if (!next) {
			setNameError(
				field,
				t(
					field === "firstName"
						? "common.validation.firstNameRequired"
						: "common.validation.lastNameRequired",
				),
			);
			return;
		}
		if (next.length > 50) {
			setNameError(field, fieldLessThanLength(t, fieldLabel, 50));
			return;
		}
		setEditingField((current) => (current === field ? null : current));
		setFieldFeedback(field, DisplayFieldFeedback.Saving);
		try {
			await updateUser.mutateAsync({
				userId: params.userId,
				data: {
					[field]: next,
					displayName: user?.displayName ?? null,
				},
			});
			setNameError(field, undefined);
			clearNameDraft(field);
			setFieldFeedback(field, DisplayFieldFeedback.Saved);
		} catch (error) {
			setFieldFeedback(field, DisplayFieldFeedback.Idle);
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	const closeStatusAction = () => {
		if (updateStatus.isPending) return;
		setPendingStatusAction(null);
		setRetireEmail("");
	};
	const confirmStatusAction = async () => {
		if (!pendingStatusAction) return;
		const action = pendingStatusAction;
		try {
			await updateStatus.mutateAsync({
				userId: params.userId,
				data: { status: platformUserStatusApiValue(action) },
			});
			showSuccessToast({
				message: t(
					`admin.users.drawer.platform.statusActions.toast.${
						action === "activate"
							? "activated"
							: action === "suspend"
								? "suspended"
								: action === "archive"
									? "archived"
									: "retired"
					}`,
					{ name: memberName },
				),
			});
			setPendingStatusAction(null);
			setRetireEmail("");
			if (action === "archive" || action === "retire") {
				navigate({
					to: "/admin/users/organization",
					replace: true,
				});
			}
		} catch (error) {
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	const nameField = (field: NameField, label: string) => {
		const isEditing = editingField === field;
		const draft = draftByField[field] ?? serverName(field);
		const error = errorByField[field];
		return (
			<DisplayInput
				label={label}
				value={isEditing ? draft : serverName(field) || undefined}
				state={
					isEditing
						? DisplayFieldState.Active
						: error
							? DisplayFieldState.Error
							: DisplayFieldState.Enabled
				}
				feedback={feedbackByField[field] ?? DisplayFieldFeedback.Idle}
				hintText={error}
				showHintText={Boolean(error)}
				showEdit={canEditName}
				editValue={isEditing ? draft : undefined}
				onEdit={canEditName ? () => beginNameEdit(field) : undefined}
				onEditChange={(value) => {
					setDraftByField((prev) => ({ ...prev, [field]: value }));
					setNameError(field, undefined);
				}}
				onEditCommit={() => void commitName(field)}
				onEditCancel={() => cancelNameEdit(field)}
			/>
		);
	};

	return (
		<div className="flex w-full flex-col">
			{nameField("firstName", t("User.FirstName", { useFieldInfo: true }))}
			{nameField("lastName", t("User.LastName", { useFieldInfo: true }))}
			<DisplayInput
				label={t("User.Email", { useFieldInfo: true })}
				value={user?.email}
				showEdit={false}
			/>
			<DisplaySelect
				label={t("admin.users.tabs.organization.details.platformStatus")}
				data-testid="platform-user-status-field"
				value={statusLabel || undefined}
				valueType={DisplaySelectValueType.Badge}
				badgeVariant={DisplaySelectBadgeVariant.Dot}
				showValueIcon={false}
				badgeColor={statusBadgeColor}
				showEdit={statusActions.length > 0}
				options={
					statusActions.length > 0
						? statusActions.map((action) => ({
								id: action,
								label: t(
									`admin.users.drawer.platform.statusActions.${action}.action`,
								),
								testId: `platform-user-status-action-${action}`,
							}))
						: undefined
				}
				onSelectionChange={(key) => {
					setRetireEmail("");
					setPendingStatusAction(key as PlatformUserStatusAction);
				}}
			/>
			<DisplayInput
				label={t("admin.users.drawer.platform.details.userId")}
				value={formatId(user?.id) || undefined}
				copyable
				copyText={user?.id}
				showEdit={false}
			/>
			{showResetPasswordSection && (
				<div className="flex items-center justify-end pt-2">
					<Button
						color="secondary"
						size="sm"
						data-testid="platform-org-user-password-reset-btn"
						onClick={() => setIsResetPasswordOpen(true)}
					>
						{t(
							"admin.users.tabs.organization.details.organizations.forcePasswordReset.title",
						)}
					</Button>
				</div>
			)}

			<PlatformUserStatusActionModal
				isOpen={pendingStatusAction != null}
				variant={pendingStatusAction ?? "suspend"}
				email={retireEmail}
				userEmail={user?.email ?? ""}
				userName={memberName}
				isLoading={updateStatus.isPending}
				onEmailChange={setRetireEmail}
				onConfirm={confirmStatusAction}
				onCancel={closeStatusAction}
			/>
			<ConfirmationModal
				isOpen={isResetPasswordOpen}
				setIsOpen={setIsResetPasswordOpen}
				title={t(
					"admin.users.tabs.organization.details.organizations.forcePasswordReset.title",
				)}
				description={t(
					"admin.users.tabs.organization.details.organizations.forcePasswordReset.description",
				)}
				icon={() => <RefreshCw01 className="text-brand-500" size={32} />}
				iconBgColor="!bg-[var(--color-bg-brand-primary)]"
				confirmText={t(
					"admin.users.tabs.organization.details.organizations.resetPassword.confirmText",
				)}
				cancelText={t("common.actions.cancel")}
				confirmTestId="platform-org-user-password-reset-confirm-btn"
				onConfirm={handleResetPassword}
				onCancel={() => setIsResetPasswordOpen(false)}
				confirmColor="primary"
			/>
		</div>
	);
}
